import type { MaintenanceChange, MaintenanceMode } from '../services/maintenanceApi';

/**
 * The arithmetic and wording behind a "Coming soon" window.
 *
 * A coming-soon server is a new pack shown to the public before it opens. Its
 * release time, when staff give one, is unix seconds from the bot and is only
 * ever turned into words in the viewer's own zone. The countdown is a promise,
 * not a switch: the server opens when somebody ends the window, so once the
 * clock passes zero every surface says "Opening any moment" rather than
 * pretending the doors are already open.
 *
 * Everything here is pure so the status page, the config pages and the tests
 * agree on what a given second looks like.
 */

export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Whole milliseconds left, never negative. */
  remainingMs: number;
  /** The release time has been reached. */
  passed: boolean;
}

/** Time left until `releaseAt` (unix seconds) as of `nowMs`. */
export function countdownParts(releaseAt: number, nowMs: number): CountdownParts {
  const remainingMs = Math.max(0, releaseAt * 1000 - nowMs);
  // Round up to the next whole second, so the display reaches 00s at the
  // moment of release rather than a second before it.
  const total = Math.ceil(remainingMs / 1000);
  return {
    days: Math.floor(total / 86_400),
    hours: Math.floor((total % 86_400) / 3_600),
    minutes: Math.floor((total % 3_600) / 60),
    seconds: total % 60,
    remainingMs,
    passed: remainingMs <= 0,
  };
}

const two = (n: number) => String(n).padStart(2, '0');

/**
 * "2d 04h 13m 09s". Leading zero units are dropped, so the last day reads
 * "04h 13m 09s" rather than "0d 04h…". `seconds: false` stops at minutes, for
 * readers who asked the page to keep still.
 */
export function formatCountdown(parts: CountdownParts, { seconds = true }: { seconds?: boolean } = {}): string {
  let { days, hours, minutes } = parts;
  if (!seconds) {
    // Without seconds the minute is rounded up, so "00m" is never shown while
    // time remains and the display still reaches zero at the release.
    const total = Math.ceil(parts.remainingMs / 60_000);
    days = Math.floor(total / 1_440);
    hours = Math.floor((total % 1_440) / 60);
    minutes = total % 60;
  }
  const units: string[] = [];
  if (days > 0) units.push(`${days}d`);
  if (days > 0 || hours > 0) units.push(`${two(hours)}h`);
  units.push(`${two(minutes)}m`);
  if (seconds) units.push(`${two(parts.seconds)}s`);
  return units.join(' ');
}

/** A release time in the viewer's zone, e.g. "Thu 1 Oct 2026, 6:30 pm". */
export function formatReleaseAt(releaseAt: number, locale?: string): string {
  return new Date(releaseAt * 1000).toLocaleString(locale, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/** The viewer's zone name, for labelling a datetime-local input. */
export function localZoneName(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time';
  } catch {
    return 'your local time';
  }
}

/** Unix seconds as a `datetime-local` value in the viewer's zone, e.g. "2026-10-01T18:30". */
export function releaseInputValue(releaseAt: number | null): string {
  if (releaseAt === null || !Number.isFinite(releaseAt)) return '';
  const at = new Date(releaseAt * 1000);
  return `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}T${two(at.getHours())}:${two(at.getMinutes())}`;
}

export type ReleaseInput =
  | { ok: true; releaseAt: number | null }
  | { ok: false; error: string };

/**
 * Read a `datetime-local` value. The value carries no zone, and the spec reads
 * a zoneless date-time as local, which is exactly what the person typing it
 * meant. Empty is valid and means "no time given"; anything else must parse
 * and be in the future, because the bot refuses a release time in the past.
 */
export function parseReleaseInput(value: string, nowMs: number): ReleaseInput {
  const trimmed = value.trim();
  if (!trimmed) return { ok: true, releaseAt: null };
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (!match) return { ok: false, error: 'Enter a date and time.' };
  const [, y, mo, d, h, mi, s] = match.map(Number);
  const at = new Date(y, mo - 1, d, h, mi, Number.isFinite(s) ? s : 0, 0);
  // new Date() rolls 31 Feb into March; a date that moved was not a date.
  if (Number.isNaN(at.getTime()) || at.getMonth() !== mo - 1 || at.getDate() !== d) {
    return { ok: false, error: 'That is not a real date.' };
  }
  const releaseAt = Math.floor(at.getTime() / 1000);
  if (releaseAt * 1000 <= nowMs) return { ok: false, error: 'The release time must be in the future.' };
  return { ok: true, releaseAt };
}

/**
 * The release time a set of coming-soon servers share, or undefined when they
 * disagree. `null` is a real answer: they agree that no time was given.
 */
export function sharedReleaseAt(entries: { releaseAt: number | null }[]): number | null | undefined {
  if (entries.length === 0) return undefined;
  const first = entries[0].releaseAt;
  return entries.every(entry => entry.releaseAt === first) ? first : undefined;
}

/** What a finished change is called on the page that made it. */
export function describeMaintenanceChange(
  result: MaintenanceChange,
  requested: { maintenance: boolean; mode?: MaintenanceMode; releaseAt?: number | null },
  /** Whether the servers were coming soon before an end, so it can be called opening. */
  wasComingSoon = false,
): string {
  const count = result.changed.length;
  const servers = `${count} ${count === 1 ? 'server' : 'servers'}`;
  const soon = requested.maintenance && requested.mode === 'coming_soon';
  if (count === 0) {
    if (result.unknown.length > 0) return `Mr. Roboto does not know ${result.unknown.join(', ')}.`;
    return requested.maintenance
      ? soon ? 'Already coming soon with that release time.' : 'Already in maintenance.'
      : 'Already out of maintenance.';
  }
  const release = requested.releaseAt ? ` Opens ${formatReleaseAt(requested.releaseAt)}.` : '';
  const text = !requested.maintenance
    ? wasComingSoon ? `${servers} opened to the public.` : `${servers} taken out of maintenance.`
    : soon ? `${servers} marked coming soon.${release}` : `${servers} put into maintenance.`;
  return text + (result.announced ? '' : ' Discord was not notified.');
}
